import React, { useEffect, useState } from "react";
import {
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import {
  validateIntegerLength,
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import {
  departmentsTooltip,
  golfTooltipHeaders,
  spaTooltipHeaders,
} from "../../../constants/TooltipInformation";
import AlertComponent from "../../subcomponents/AlertComponent";

const SpaServiceLocations = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [locationCode, setLocationCode] = useState<string | null>(null);
  const [locationName, setLocationName] = useState<string | null>(null);
  const [capacity, setCapacity] = useState<number | "">("");
  const [locationDescription, setLocationDescription] = useState<string | null>(
    null,
  );
  const [codeFieldError, setCodeFieldError] = useState<boolean>(false);
  const [codeFieldErrorMessage, setCodeFieldErrorMessage] =
    useState<string>("");
  const [nameFieldError, setNameFieldError] = useState<boolean>(false);
  const [nameFieldErrorMessage, setNameFieldErrorMessage] =
    useState<string>("");
  const [capacityFieldError, setCapacityFieldError] = useState<boolean>(false);
  const [capacityFieldErrorMessage, setCapacityFieldErrorMessage] =
    useState<string>("");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const validateServiceLocationFields = (): boolean => {
    if (!locationCode || locationCode.length === 0) {
      setAlertMessage("Location Code is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeFieldError(false);
    setCodeFieldErrorMessage("");
    if (
      !validateString(
        locationCode,
        validationLength.serviceLocationCode,
        validationType.CODE,
      )
    ) {
      setCodeFieldError(true);
      setCodeFieldErrorMessage(validationMessages.serviceLocationCode);
      return false;
    }

    if (!locationName || locationName.length === 0) {
      setAlertMessage("Location Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setNameFieldError(false);
    setNameFieldErrorMessage("");
    if (
      !validateString(
        locationName,
        validationLength.serviceLocationName,
        validationType.NAME,
      )
    ) {
      setNameFieldError(true);
      setNameFieldErrorMessage(validationMessages.serviceLocationName);
      return false;
    }

    if (capacity === "" || capacity === null) {
      setAlertMessage("Capacity is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      setCapacityFieldError(true);
      setCapacityFieldErrorMessage("Capacity is mandatory");
      return false;
    }
    setCapacityFieldError(false);
    setCapacityFieldErrorMessage("");
    if (
      !validateIntegerLength(
        Number(capacity),
        validationLength.serviceLocationCapacity,
      )
    ) {
      setCapacityFieldError(true);
      setCapacityFieldErrorMessage(validationMessages.serviceLocationCapacity);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(listForDuplicate, DNDFieldsEnum.LOCATIONCODE, locationCode)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const updateItem = () => {
    if (!validateServiceLocationFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.serviceLocations} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateServiceLocationFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setLocationCode(list["Location Code"] ?? null);
      setLocationName(list["Location Name"] ?? null);
      setCapacity(
        list["Capacity"] !== undefined && list["Capacity"] !== null
          ? Number(list["Capacity"])
          : "",
      );
      setLocationDescription(list["Location Description"] ?? null);
    } else {
      setLocationCode(null);
      setLocationName(null);
      setCapacity("");
      setLocationDescription(null);
      setCodeFieldError(false);
      setCodeFieldErrorMessage("");
      setNameFieldError(false);
      setNameFieldErrorMessage("");
      setCapacityFieldError(false);
      setCapacityFieldErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Location Code": locationCode,
    "Location Name": locationName,
    Capacity: Number(capacity),
    "Location Description": locationDescription,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Service Locations
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.serviceLocationHeader ??
                  golfTooltipHeaders?.categoryGroup ??
                  "Service Locations",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Location Code</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceLocationLocationCode,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Location Code"
              value={locationCode ?? ""}
              error={codeFieldError}
              helperText={codeFieldError ? codeFieldErrorMessage : ""}
              onChange={(e) => setLocationCode(e.target.value || null)}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Location Name</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceLocationLocationName,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Location Name"
              value={locationName ?? ""}
              onChange={(e) => setLocationName(e.target.value || null)}
              error={nameFieldError}
              helperText={nameFieldError ? nameFieldErrorMessage : ""}
              fullWidth
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Capacity</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceLocationMaximumNumberOfGuests,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              type="number"
              variant="outlined"
              placeholder="Capacity"
              value={capacity}
              error={capacityFieldError}
              helperText={capacityFieldError ? capacityFieldErrorMessage : ""}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  setCapacity("");
                  setCapacityFieldError(false);
                  setCapacityFieldErrorMessage("");
                  return;
                }
                if (!/^\d{1,3}$/.test(value)) {
                  return;
                }
                const parsed = parseInt(value, 10);
                setCapacity(Number.isNaN(parsed) ? "" : parsed);
                setCapacityFieldError(false);
                setCapacityFieldErrorMessage("");
              }}
              fullWidth
              inputProps={{
                min: 0,
                max: 999,
              }}
            />
          </Stack>
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Location Description</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  spaTooltipHeaders.serviceLocationLocationDescription,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>

            <TextField
              variant="outlined"
              placeholder="Location Description"
              value={locationDescription ?? ""}
              onChange={(e) => setLocationDescription(e.target.value || null)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Service
            Location
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Service Locations Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.LOCATIONCODE}
              name={DNDFieldsEnum.LOCATIONNAME}
            />
          ) : (
            <Typography variant="body2">
              No Service Locations Available
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaServiceLocations;
